import { useState } from "react"
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { LinkedScenariosCell } from "../linked-scenarios-cell"
import { cloneRateCard } from "routes/rate-cards/controller/clone-rate-card"
import { archiveRateCard } from "routes/rate-cards/controller/archive-rate-card"
import { restoreRateCard } from "routes/rate-cards/controller/restore-rate-card"
import { formatDate, getCurrencySymbol } from "../format-utils"
import { exportRateCardCsv } from "./export-rate-card-csv"

export function RateCardRow({ item }) {
    const lines = item.lines || []
    const pricedCount = lines.filter(isPriced).length
    const totalCount = lines.length
    const [anchorEl, setAnchorEl] = useState(null)
    const { selectedIds, onToggleSelect, canManageOptimiser } = useBoundContext()
    const isSelected = selectedIds?.has(item.id)

    return (
        <ListItemButton
            data-cy="rate-card-row"
            sx={{
                px: 3,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
                ...(item.isArchived && { opacity: 0.5 }),
            }}
            onClick={navigateToCard}
        >
            <ListItemBox sx={{ gap: 3 }}>
                <If value={canManageOptimiser}>
                    <Checkbox
                        size="small"
                        checked={isSelected || false}
                        onChange={handleToggleCheck}
                        onClick={stopProp}
                        sx={{ p: 0, mr: -1, flexShrink: 0 }}
                    />
                </If>
                <Box sx={{ flex: 1, minWidth: 200, overflow: "hidden" }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <TruncatedTypography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                        </TruncatedTypography>
                        <If value={item.needsReview}>
                            <Chip
                                label="Needs Review"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 20, fontSize: 11 }}
                            />
                        </If>
                    </Stack>
                    <If value={item.description}>
                        <TruncatedTypography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>
                            {item.description}
                        </TruncatedTypography>
                    </If>
                </Box>
                <Box sx={{ width: 160 }}>
                    <If value={item.skillMapName}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0,
                                cursor: "pointer",
                                "&:hover .sm-name": { textDecoration: "underline" },
                            }}
                            onClick={goToSkillMap}
                        >
                            <Iconify
                                icon="icon-park-twotone:mind-mapping"
                                width={20}
                                height={20}
                                sx={{ color: "text.primary", flexShrink: 0, mr: 1 }}
                            />
                            <Typography className="sm-name" variant="body2" sx={{ color: "text.primary" }} noWrap>
                                {item.skillMapName}
                            </Typography>
                            <If value={item.skillMapIsArchived}>
                                <Chip
                                    label="Archived"
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: 11, ml: 1 }}
                                />
                            </If>
                        </Box>
                        <Else>
                            <Typography variant="body2" color="text.secondary">
                                -
                            </Typography>
                        </Else>
                    </If>
                </Box>
                <Box sx={{ width: 140 }}>
                    <LinkedScenariosCell scenarios={item.linkedScenarios} />
                </Box>
                <Box sx={{ width: 80, textAlign: "right" }}>
                    <Typography variant="body2">{currencyLabel()}</Typography>
                </Box>
                <Box sx={{ width: 120, textAlign: "right" }}>
                    <Typography variant="body2">
                        {pricedCount} of {totalCount}
                    </Typography>
                </Box>
                <Box sx={{ width: 120 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {formatDate(item.modified || item.created)}
                    </Typography>
                </Box>
                <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                    <IconButton aria-label="More options" size="small" onClick={openMenu}>
                        <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                        <If value={!item.isArchived && canManageOptimiser}>
                            <MenuItem onClick={handleEdit}>
                                <ListItemIcon>
                                    <Iconify icon="solar:pen-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                        </If>
                        <If value={item.skillMapId}>
                            <MenuItem onClick={handleViewSkillMap}>
                                <ListItemIcon>
                                    <Iconify icon="icon-park-twotone:mind-mapping" width={20} />
                                </ListItemIcon>
                                <ListItemText>View Skill Map</ListItemText>
                            </MenuItem>
                        </If>
                        <MenuItem onClick={handleExportCsv}>
                            <ListItemIcon>
                                <Iconify icon="solar:document-text-bold" width={20} />
                            </ListItemIcon>
                            <ListItemText>Export CSV</ListItemText>
                        </MenuItem>
                        <If value={canManageOptimiser}>
                            <MenuItem onClick={handleDuplicate}>
                                <ListItemIcon>
                                    <Iconify icon="solar:copy-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                            <If value={!item.isArchived}>
                                <MenuItem onClick={handleArchive}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:archive-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Archive</ListItemText>
                                </MenuItem>
                                <Else>
                                    <MenuItem onClick={handleRestore}>
                                        <ListItemIcon>
                                            <Iconify icon="solar:restart-bold" width={20} />
                                        </ListItemIcon>
                                        <ListItemText>Restore</ListItemText>
                                    </MenuItem>
                                </Else>
                            </If>
                        </If>
                    </Menu>
                </Box>
            </ListItemBox>
        </ListItemButton>
    )

    function isPriced(l) {
        return l.hourlyRate > 0
    }

    function currencyLabel() {
        const code = item.currency || "GBP"
        return `${code} (${getCurrencySymbol(code)})`
    }

    function openMenu(e) {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }

    function closeMenu(e) {
        if (e) e.stopPropagation()
        setAnchorEl(null)
    }

    function stopProp(e) {
        e.stopPropagation()
    }

    function handleToggleCheck(e) {
        e.stopPropagation()
        onToggleSelect?.(item.id)
    }

    function navigateToCard() {
        navigate(`/app/optimiser/rate-cards/${item.id}`)
    }

    function goToSkillMap(e) {
        e.stopPropagation()
        if (!item.skillMapId) return
        navigate(`/app/optimiser/skill-mappings/${item.skillMapId}`)
    }

    function handleEdit(e) {
        e.stopPropagation()
        closeMenu()
        navigate(`/app/optimiser/rate-cards/${item.id}`)
    }

    function handleViewSkillMap(e) {
        e.stopPropagation()
        closeMenu()
        navigate(`/app/optimiser/skill-mappings/${item.skillMapId}`)
    }

    function handleExportCsv(e) {
        e.stopPropagation()
        closeMenu()
        exportRateCardCsv({ card: item })
    }

    async function handleDuplicate(e) {
        e.stopPropagation()
        closeMenu()
        const newCard = await busyWhile(duplicateCard, "Duplicating rate card...")
        if (newCard?.id) {
            navigate(`/app/optimiser/rate-cards/${newCard.id}`)
            successSnackbar("Rate card duplicated")
        }
    }

    function duplicateCard() {
        return cloneRateCard({ id: item.id })
    }

    async function handleArchive(e) {
        e.stopPropagation()
        closeMenu()
        const linked = item.linkedScenarios || []
        const scenarioWarning =
            linked.length > 0
                ? `\n\nThis rate card is used by ${linked.length} scenario${linked.length > 1 ? "s" : ""}: ${linked.map((s) => s.name).join(", ")}. Those scenarios will show an archived rate card warning.`
                : ""
        const ok = await confirm(
            `Archive "${item.name}"? It can be restored later from the Archived filter.${scenarioWarning}`,
            "Archive Rate Card"
        )
        if (!ok) return
        await busyWhile(() => archiveRateCard({ id: item.id }), "Archiving rate card...")
        successSnackbar(`Rate card "${item.name}" archived`)
    }

    async function handleRestore(e) {
        e.stopPropagation()
        closeMenu()
        await busyWhile(() => restoreRateCard({ id: item.id }), "Restoring rate card...")
        successSnackbar(`Rate card "${item.name}" restored`)
    }
}
